import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@material-ui/core'
import { DataGrid, GridToolbar } from '@mui/x-data-grid'
import moment from 'moment'
import { useHistory } from 'react-router-dom'
import styles from './QuotesPage.module.css'
import { deleteQuote, fetchQuotes, updateQuote } from '../api'
import useAuthUser from '../hooks/useAuthUser'
import { dollarFormatter } from '../utils/money'
import { buildInvoiceDraftFromQuote } from '../utils/quoteDraft'

const getStatusClass = (status = '') => {
  const normalized = String(status || '').toLowerCase()
  if (normalized === 'converted') return styles.converted
  if (normalized === 'declined') return styles.declined
  return styles.draft
}

const primaryActionStyle = { color: 'white', backgroundColor: 'rgb(25, 118, 210)' }
const warningActionStyle = { color: 'white', backgroundColor: '#f59e0b' }
const deleteActionStyle = { color: 'white', backgroundColor: 'red' }

const QuotesPage = () => {
  const history = useHistory()
  const authData = useAuthUser()
  const [quotes, setQuotes] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [declineTarget, setDeclineTarget] = useState(null)

  const loadQuotes = useCallback(async () => {
    const userId = authData?.result?._id || authData?.result?.googleId
    if (!userId) return

    setLoading(true)
    setError('')
    try {
      const { data } = await fetchQuotes({ search: userId })
      setQuotes(data?.data || [])
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to load quotes.')
    } finally {
      setLoading(false)
    }
  }, [authData])

  useEffect(() => {
    if (!authData) {
      history.push('/login')
      return
    }
    loadQuotes()
  }, [authData, history, loadQuotes])

  const openQuote = useCallback((quote) => {
    if (!quote?._id) return
    history.push(`/quote-builder/${quote._id}`)
  }, [history])

  const convertQuote = useCallback((quote) => {
    const quoteDraft = buildInvoiceDraftFromQuote(quote)
    history.push('/invoice', { quoteDraft })
  }, [history])

  const handleDeleteClick = useCallback((quote) => {
    if (!quote?._id) return
    setDeleteTarget(quote)
  }, [])

  const handleCancelDelete = useCallback(() => {
    setDeleteTarget(null)
  }, [])

  const handleDeclineClick = useCallback((quote) => {
    if (!quote?._id || quote.status === 'Declined' || quote.convertedInvoiceId) return
    setDeclineTarget(quote)
  }, [])

  const handleCancelDecline = useCallback(() => {
    setDeclineTarget(null)
  }, [])

  const handleConfirmDecline = useCallback(async () => {
    if (!declineTarget?._id) return

    try {
      const { data } = await updateQuote(declineTarget._id, { status: 'Declined' })
      setQuotes((prev) => prev.map((item) => (item._id === declineTarget._id ? data : item)))
      setDeclineTarget(null)
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to decline quote.')
    }
  }, [declineTarget])

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget?._id) return

    try {
      await deleteQuote(deleteTarget._id)
      setQuotes((prev) => prev.filter((item) => item._id !== deleteTarget._id))
      setDeleteTarget(null)
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to delete quote.')
    }
  }, [deleteTarget])

  const rows = useMemo(() => quotes.map((quote) => ({
    id: quote._id,
    quote,
    quoteNumber: quote.quoteNumber || 'Quote',
    status: quote.status || 'Draft',
    address: quote.fields?.address || '',
    units: quote.totals?.itemCount || 0,
    total: Number(quote.totals?.subtotal) || 0,
    earliestDelivery: quote.deliveryDecision?.label || '',
    updatedAt: quote.updatedAt || null,
    convertedInvoiceId: quote.convertedInvoiceId || '',
    convertedInvoiceNumber: quote.convertedInvoiceNumber || '',
  })), [quotes])

  const columns = useMemo(() => [
    {
      field: 'quoteNumber',
      headerName: 'Quote',
      minWidth: 140,
      flex: 0.8,
      renderCell: (params) => (
        <button
          type="button"
          className={styles.linkButton}
          onClick={(event) => {
            event.stopPropagation()
            openQuote(params.row.quote)
          }}
        >
          {params.value || 'Quote'}
        </button>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 130,
      flex: 0.7,
      type: 'singleSelect',
      valueOptions: ['Draft', 'Converted', 'Declined'],
      renderCell: (params) => (
        <span className={`${styles.statusPill} ${getStatusClass(params.value)}`}>
          {params.value || 'Draft'}
        </span>
      ),
    },
    {
      field: 'address',
      headerName: 'Address',
      minWidth: 240,
      flex: 1.7,
      valueGetter: (params) => params.value || '-',
    },
    {
      field: 'units',
      headerName: 'Units',
      type: 'number',
      minWidth: 90,
      flex: 0.45,
    },
    {
      field: 'total',
      headerName: 'Total',
      type: 'number',
      minWidth: 120,
      flex: 0.6,
      valueFormatter: ({ value }) => dollarFormatter.format(Number(value) || 0),
    },
    {
      field: 'earliestDelivery',
      headerName: 'Earliest delivery',
      minWidth: 180,
      flex: 1,
      valueGetter: (params) => params.value || '-',
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      type: 'dateTime',
      minWidth: 180,
      flex: 1,
      valueGetter: (params) => (params.value ? new Date(params.value) : null),
      valueFormatter: ({ value }) => (value ? moment(value).format('MM/DD/YYYY h:mm A') : '-'),
    },
    {
      field: 'convertedInvoiceNumber',
      headerName: 'Converted invoice',
      minWidth: 180,
      flex: 1,
      renderCell: (params) => {
        if (!params.row.convertedInvoiceId) return <span>-</span>
        return (
          <button
            type="button"
            className={styles.linkButton}
            onClick={(event) => {
              event.stopPropagation()
              history.push(`/invoice/${params.row.convertedInvoiceId}`)
            }}
          >
            {params.value || 'Open invoice'}
          </button>
        )
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      headerAlign: 'center',
      align: 'center',
      minWidth: 460,
      width: 460,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div className={styles.rowActions}>
          <Button
            size="small"
            style={primaryActionStyle}
            variant="contained"
            onClick={(event) => {
              event.stopPropagation()
              openQuote(params.row.quote)
            }}
          >
            Open
          </Button>
          {params.row.convertedInvoiceId ? (
            <Button
              size="small"
              style={primaryActionStyle}
              variant="contained"
              onClick={(event) => {
                event.stopPropagation()
                history.push(`/invoice/${params.row.convertedInvoiceId}`)
              }}
            >
              Invoice
            </Button>
          ) : (
            <Button
              size="small"
              style={primaryActionStyle}
              variant="contained"
              onClick={(event) => {
                event.stopPropagation()
                convertQuote(params.row.quote)
              }}
            >
              Convert
            </Button>
          )}
          {!params.row.convertedInvoiceId && params.row.status !== 'Declined' ? (
            <Button
              size="small"
              style={warningActionStyle}
              variant="contained"
              onClick={(event) => {
                event.stopPropagation()
                handleDeclineClick(params.row.quote)
              }}
            >
              Decline
            </Button>
          ) : null}
          <Button
            size="small"
            style={deleteActionStyle}
            variant="contained"
            onClick={(event) => {
              event.stopPropagation()
              handleDeleteClick(params.row.quote)
            }}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ], [convertQuote, handleDeclineClick, handleDeleteClick, history, openQuote])

  return (
    <div className={styles.pageLayout}>
      <Dialog open={Boolean(declineTarget)} onClose={handleCancelDecline}>
        <DialogTitle>Decline Quote</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Mark {declineTarget?.quoteNumber || 'this quote'} as declined? It will stay in the quotes list with a Declined status.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDecline} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDecline} color="secondary" variant="contained">
            Decline
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(deleteTarget)} onClose={handleCancelDelete}>
        <DialogTitle>Delete Quote</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete {deleteTarget?.quoteNumber || 'this quote'}? This will remove it from the quotes list.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="secondary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Container style={{ width: '90%', maxWidth: 1400 }}>
        <div className={styles.buttons}>
          <h1>Quotes</h1>
          <button
            type="button"
            className={styles.btn}
            onClick={() => history.push('/quote-builder')}
          >
            New Quote
          </button>
        </div>

        {error ? <div className={styles.warning}>{error}</div> : null}

        <div className={styles.gridScroller}>
          <div className={styles.gridInner}>
            <DataGrid
              autoHeight
              loading={loading}
              rows={rows}
              columns={columns}
              pageSize={25}
              rowsPerPageOptions={[10, 25, 50, 100]}
              components={{ Toolbar: GridToolbar }}
              componentsProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 300 } } }}
              disableRowSelectionOnClick
              onRowDoubleClick={(params) => openQuote(params.row.quote)}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'updatedAt', sort: 'desc' }],
                },
              }}
              localeText={{
                noRowsLabel: 'No saved quotes yet.',
              }}
              sx={{
                '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '6px' },
                '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '8px' },
                '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '12px' },
              }}
            />
          </div>
        </div>
      </Container>
    </div>
  )
}

export default QuotesPage
